import {
    ChevronLeftIcon,
    ChevronRightIcon,
    TagIcon,
    PlusIcon,
    PencilIcon
} from '@heroicons/react/outline';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';
import ReactPaginate from 'react-paginate';
import '../../Pagination.css';
import {
    useGetCategoriesPageChangeMutation,
    useGetPaginatedUserCategoriesQuery
} from '../../Features/categories';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';
import { useTheme } from '../../ThemeContext';

function ManageCategoriesView() {
    const { darkMode } = useTheme();

    const navigate = useNavigate();

    const [pager, setPager] = useState("");
    const [progress, setProgress] = useState(0);
    const { isOnline } = useIsOnline();

    const [numRowsPerPage, setNumRowsPerPage] = useState(10);
    const [currentItems, setCurrentItems] = useState([]);
    const [pageCount, setPageCount] = useState(0);

    const user = useSelector(selectCurrentUser);

    const {
        data: categories,
        isLoading,
        isSuccess,
        isError,
        error
    } = useGetPaginatedUserCategoriesQuery({
        user_id: user.id,
        paginate: numRowsPerPage,
        page: pager
    });

    const [input, setInput] = useState({
        search: ''
    });

    useEffect(() => {
        setProgress(30);

        if (isLoading) {
            setProgress(80);
        }

        if (isSuccess && categories?.data) {
            setCurrentItems(categories.data.data || []);
            setPageCount(
                Math.ceil(
                    categories.data.total / numRowsPerPage
                )
            );
        }

        setProgress(100);

        return () => {};
    }, [categories, isLoading, isSuccess, numRowsPerPage]);

    const [pageChange] =
        useGetCategoriesPageChangeMutation();

    const onChangePageRows = async (rows) => {
        const rowsPerPage = window.parseInt(rows);

        if (!isOnline) {
            return swal(
                "No Internet connection",
                "Please check Internet connection and try again"
            );
        }

        try {
            setProgress(80);

            const resp = await pageChange({
                user_id: user.id,
                paginate: rowsPerPage,
                page: 1
            }).unwrap();

            if (resp.success) {
                setCurrentItems(resp.data.data);

                setPageCount(
                    Math.ceil(
                        resp.data.total / rowsPerPage
                    )
                );

                setNumRowsPerPage(rowsPerPage);
                setPager(1);
                setProgress(100);
            }
        } catch (err) {
            setProgress(100);

            swal(
                "Error",
                err.message,
                "error"
            );
        }
    };

    const handlePageChange = async (currentPage) => {
        const pageNum =
            window.parseInt(currentPage) + 1;

        if (!isOnline) {
            return swal(
                "No Internet connection",
                "Please check Internet connection and try again"
            );
        }

        try {
            setProgress(80);

            const resp = await pageChange({
                user_id: user.id,
                paginate: numRowsPerPage,
                page: pageNum
            }).unwrap();

            if (resp.success) {
                setCurrentItems(resp.data.data);

                setPageCount(
                    Math.ceil(
                        resp.data.total / numRowsPerPage
                    )
                );

                setPager(pageNum);
                setProgress(100);
            }
        } catch (err) {
            setProgress(100);

            swal(
                "Error",
                err.message,
                "error"
            );
        }
    };

    const editCategory = (e, id) => {
        e.stopPropagation();

        if (!isOnline) {
            return swal(
                "No Internet connection",
                "Please check Internet connection and try again"
            );
        }

        navigate("/client/edit-category/" + id);
    };

    const truncateText = (str) => {
        if (!str) return '';

        return str.length > 35
            ? str.substring(0, 35) + "..."
            : str;
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            <div className="w-full max-w-5xl mx-auto px-3 sm:px-5 lg:px-6 pt-4 sm:pt-6">

                {/* HEADER */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">

                    <div className="flex items-center gap-3">

                        <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                            <TagIcon className="w-6 h-6 text-[#80b643]" />
                        </div>

                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white">
                                Categories
                            </h1>

                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                Manage ticket and route categories
                            </p>
                        </div>

                    </div>

                    <Link
                        to="/client/add-category"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition"
                    >
                        <PlusIcon className="w-4 h-4" />
                        Add Category
                    </Link>

                </div>

                {/* MAIN CARD */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

                    {/* CARD HEADER */}
                    <div className="px-4 sm:px-5 py-4 border-b border-gray-200 dark:border-gray-800">

                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

                            <div>
                                <h2 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white">
                                    Category List
                                </h2>

                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    Select a category to edit its information
                                </p>
                            </div>

                            {isSuccess && categories?.data && (
                                <div className="inline-flex self-start sm:self-auto items-center px-2.5 py-1 rounded-full bg-[#80b643]/10 text-[#80b643] text-xs font-medium">
                                    {categories.data.total}{" "}
                                    {categories.data.total === 1
                                        ? "category"
                                        : "categories"}
                                </div>
                            )}

                        </div>

                    </div>

                    {/* LOADING */}
                    {isLoading && (
                        <div className="flex items-center justify-center py-16">
                            <div className="text-center">

                                <div className="w-9 h-9 border-3 border-gray-200 dark:border-gray-700 border-t-[#80b643] rounded-full animate-spin mx-auto"></div>

                                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                                    Loading categories...
                                </p>

                            </div>
                        </div>
                    )}

                    {/* TABLE */}
                    {!isLoading && currentItems.length > 0 && (

                        <div className="overflow-x-auto">

                            <table className="w-full min-w-[500px]">

                                <thead className="bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-700">

                                    <tr>

                                        <th
                                            scope="col"
                                            className="px-4 sm:px-6 py-3 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                        >
                                            Category
                                        </th>

                                        <th
                                            scope="col"
                                            className="px-4 sm:px-6 py-3 text-right text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28"
                                        >
                                            Action
                                        </th>

                                    </tr>

                                </thead>

                                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">

                                    {currentItems.map((data) => (

                                        <tr
                                            key={data.id}
                                            onClick={(e) =>
                                                editCategory(e, data.id)
                                            }
                                            className="group hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer transition"
                                        >

                                            {/* CATEGORY */}
                                            <td className="px-4 sm:px-6 py-4">

                                                <div className="flex items-center gap-3">

                                                    <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center flex-shrink-0">

                                                        <span className="text-sm font-semibold text-[#80b643]">
                                                            {data.name
                                                                ? data.name
                                                                    .charAt(0)
                                                                    .toUpperCase()
                                                                : "?"}
                                                        </span>

                                                    </div>

                                                    <div className="min-w-0">

                                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                                                            {truncateText(data.name)}
                                                        </p>

                                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                                            Category
                                                        </p>

                                                    </div>

                                                </div>

                                            </td>

                                            {/* ACTION */}
                                            <td className="px-4 sm:px-6 py-4 text-right">

                                                <button
                                                    type="button"
                                                    onClick={(e) =>
                                                        editCategory(
                                                            e,
                                                            data.id
                                                        )
                                                    }
                                                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-300 hover:border-[#80b643] hover:text-[#80b643] transition"
                                                >
                                                    <PencilIcon className="w-3.5 h-3.5" />
                                                    Edit
                                                </button>

                                            </td>

                                        </tr>

                                    ))}

                                </tbody>

                            </table>

                        </div>

                    )}

                    {/* EMPTY STATE */}
                    {!isLoading &&
                        (currentItems.length === 0 || isError) && (

                            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">

                                <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">

                                    <TagIcon className="w-7 h-7 text-gray-400 dark:text-gray-500" />

                                </div>

                                <h3 className="mt-4 text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    No categories found
                                </h3>

                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 max-w-sm">
                                    You haven't created any categories yet.
                                    Create a category to start organizing
                                    your routes and tickets.
                                </p>

                                <Link
                                    to="/client/add-category"
                                    className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition"
                                >
                                    <PlusIcon className="w-4 h-4" />
                                    Add Category
                                </Link>

                            </div>

                        )}

                    {/* PAGINATION */}
                    {!isLoading && currentItems.length > 0 && (

                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-3 sm:px-4 py-4 border-t border-gray-200 dark:border-gray-800">

                            {/* PAGINATION */}
                            <div className="flex items-center">

                                <ReactPaginate
                                    breakLabel="..."
                                    nextLabel={
                                        <ChevronRightIcon className="h-7 w-7 text-gray-400 dark:text-gray-300" />
                                    }
                                    onPageChange={(e) =>
                                        handlePageChange(e.selected)
                                    }
                                    pageRangeDisplayed={3}
                                    pageCount={pageCount}
                                    previousLabel={
                                        <ChevronLeftIcon className="h-7 w-7 text-gray-400 dark:text-gray-300" />
                                    }
                                    renderOnZeroPageCount={null}
                                    containerClassName="pagination"
                                    pageLinkClassName="page-num"
                                    previousLinkClassName="controlBtn"
                                    nextLinkClassName="controlBtn"
                                    activeLinkClassName={`${
                                        darkMode ? '' : 'active'
                                    } dark:text-white bg-white dark:bg-gray-700 hover:dark:text-white hover:dark:bg-gray-600`}
                                />

                            </div>

                            {/* ROWS PER PAGE */}
                            <div className="flex items-center gap-2">

                                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                    Rows per page
                                </span>

                                <select
                                    value={numRowsPerPage}
                                    onChange={(e) =>
                                        onChangePageRows(
                                            e.target.value
                                        )
                                    }
                                    className="h-8 px-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-300 outline-none focus:border-[#80b643] focus:ring-1 focus:ring-[#80b643]"
                                >
                                    <option value="10">
                                        10
                                    </option>

                                    <option value="25">
                                        25
                                    </option>

                                    <option value="50">
                                        50
                                    </option>

                                    <option value="100">
                                        100
                                    </option>
                                </select>

                            </div>

                        </div>

                    )}

                </div>

            </div>

        </div>
    );
}

export default ManageCategoriesView;